/**
 * @fileoverview Sign-out COMPLETO — a única porta de saída de conta do app.
 *
 * Por que existe: o app tinha 4 botões de "Sair da conta" (Settings, Profiles,
 * AccountGate, OnboardingMode) e cada um derrubava um PEDAÇO diferente da
 * sessão. O gate de segurança reprovou: nenhum deles limpava o token, e dois
 * nem esqueciam a credencial Xtream lembrada. Numa TV de sala (aparelho
 * COMPARTILHADO) isso vaza credencial e dado pessoal entre pessoas:
 *
 *   A sai da conta → B pareia a conta DELE → RootRoute manda pra /login →
 *   LoginScreen faz getRemembered(providerCode) e pré-preenche usuário+senha
 *   do A → 1 OK e B está usando a assinatura do A.
 *
 * Sign-out é um INVARIANTE de segurança, não um detalhe de cada tela: 4 portas
 * têm que ter a MESMA fechadura. Toda tela que desloga chama `signOut()` — não
 * reimplemente os passos abaixo numa tela nova.
 *
 * ESCOPO: sign-out mexe em SESSÃO, e só. **Sair da conta e apagar a conta são
 * coisas diferentes** (decisão do dono do produto, 2026-07). O dado pessoal do
 * usuário (histórico, favoritos, perfis) SOBREVIVE ao sign-out: apagá-lo é uma
 * ação separada e explícita ("apagar dados deste aparelho"), nunca um efeito
 * colateral de sair. `local-db.clearPersonalDataForProvider()` existe e está
 * pronta pra essa ação futura — mas NÃO é chamada aqui.
 *
 * ORDEM (cada passo depende do anterior — não reordene sem ler isto):
 *
 *  1. FLUSH do pendente. Precisa dos tokens/sessão VIVOS pra autenticar, então
 *     vem ANTES de qualquer limpeza. É a última chance de não perder o
 *     progresso que o usuário acabou de assistir.
 *  2. TEARDOWN do sync (sessão Swipe + outbox + cursores). Local, sem rede.
 *  3. ESQUECE a credencial Xtream lembrada (mata o pré-preenchimento do login).
 *  4. TOKENS: limpa local (garantido) + revoga o refresh no servidor (best-effort).
 *  5. STORE: logout('user').
 *
 * REDE NUNCA TRAVA O SIGN-OUT. Todo passo que fala com o servidor (1 e 4) é
 * best-effort e limitado no tempo: numa TV offline, um flush pendurado deixaria
 * o usuário PRESO na conta — que é exatamente o bug que esta frente veio
 * consertar. Falhou/estourou o prazo? Loga e segue derrubando o estado local.
 * O estado local é a fronteira de segurança que o usuário do sofá enxerga.
 */

import { api } from './api'
import { clearTokens, getTokens } from './auth-store'
import { forgetRemembered } from './login-memory'
import { useSessionStore } from './store'
import { clearSwipeLinked } from './swipe-linked-flag'
import { endSwipeSync, flushNow, hasSwipeSession } from './sync'

/** Teto do flush final. Numa TV com rede ruim o flush pode pendurar; sign-out
 *  que pendura = usuário preso na conta. 3s é o suficiente pro caso normal e
 *  curto o bastante pra não parecer travamento no sofá. */
const FLUSH_TIMEOUT_MS = 3000

/** Aguarda a promise resolver OU o prazo estourar. NUNCA rejeita: o caller
 *  segue em frente de qualquer jeito. */
async function settleWithin(p: Promise<unknown>, ms: number): Promise<void> {
  await Promise.race([
    p.catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, ms)),
  ])
}

/**
 * Encerra a SESSÃO por completo: nenhuma credencial, token ou sessão de sync do
 * usuário que saiu continua utilizável neste aparelho.
 *
 * NÃO apaga dado pessoal (histórico/favoritos/perfis) — sair da conta não é
 * apagar a conta. Ver "ESCOPO" no topo do arquivo.
 *
 * Não navega e não mexe em rota — a tela decide pra onde ir depois (cada uma
 * tem um destino diferente). Só derruba estado. Nunca lança: sign-out que
 * quebra no meio deixa o aparelho num estado pior que o inicial.
 */
export async function signOut(): Promise<void> {
  // Identidade capturada ANTES de derrubar nada: depois do logout() o store não
  // sabe mais de quem era a credencial que precisa ser esquecida.
  const session = useSessionStore.getState()
  const scopeKey = session.providerCode
    ? session.providerCode
    : session.direct
      ? `direct:${session.direct.listName}`
      : null
  const refreshToken = getTokens()?.refreshToken ?? null

  // 1. Última chance de subir o progresso pendente (precisa da sessão viva).
  //    Limitado no tempo: TV offline não pode prender o usuário na conta.
  if (hasSwipeSession()) {
    await settleWithin(flushNow(), FLUSH_TIMEOUT_MS)
  }

  // 2. Teardown do sync: encerra a sessão Swipe, zera outbox/cursores. Tudo
  //    local (sem rede) — seguro aguardar. Sem flushFirst: o flush já foi no
  //    passo 1, e refazê-lo aqui seria um segundo caminho de rede sem teto.
  //    Zera a SESSÃO de sync, não o dado sincronizado (que fica na conta).
  try {
    await endSwipeSync()
  } catch (err) {
    console.warn('[sign-out] teardown do sync falhou (segue)', err)
  }

  // 2b. Marcador durável de vínculo (UX-only do gate). Sign-out é TERMINAL: o
  //     usuário ESCOLHEU sair, não é a sessão que caiu. Some o marcador pra o
  //     próximo boot mostrar o gate frio (entry/link), não "reconecte". No 401
  //     recuperável, ao contrário, o marcador SOBREVIVE — por isso ele NÃO mora
  //     no clearSwipeSession e é limpo aqui, explicitamente.
  clearSwipeLinked()

  // 3. Credencial Xtream lembrada: é ela que pré-preenche o LoginScreen do
  //    próximo usuário. Mesmo padrão de SettingsScreen/ProfilesScreen ("TV pode
  //    ser de terceiro"), agora em TODAS as portas de saída.
  if (scopeKey) forgetRemembered(scopeKey)

  // 4. Tokens. O LOCAL morre já e de forma síncrona — é o que protege o próximo
  //    usuário e não pode depender da rede. A revogação do refresh no servidor é
  //    best-effort (o refresh vai no corpo, capturado antes do clear).
  clearTokens()
  try {
    await settleWithin(api.logout(refreshToken ?? undefined), FLUSH_TIMEOUT_MS)
  } catch (err) {
    console.warn('[sign-out] revogação do refresh no servidor falhou (segue)', err)
  }

  // 5. Store por último: as telas reagem a `isAuthed` e o RootRoute re-decide a
  //    rota no mesmo tick. Com tudo acima já limpo, ele nunca vê estado meio-vivo.
  session.logout('user')
}
